import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from 'services/api';
import useAuthStore from 'stores/authStore';
import { Button } from 'components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Badge } from 'components/ui/badge';
import { Input } from 'components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'components/ui/tabs';
import { Textarea } from 'components/ui/textarea';
import { 
  ClipboardList, Users, AlertTriangle, FileText, Settings, Loader2,
  Search, ChevronRight, Calendar, Bell, CheckCircle2, Clock, Eye
} from 'lucide-react';
import { toast } from 'sonner';

export default function CaseManagerDashboard() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCase, setSelectedCase] = useState(null);
  const [caseDetail, setCaseDetail] = useState(null);
  const [newNote, setNewNote] = useState('');

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await api.get('/case-manager/dashboard');
      setDashboard(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchCaseDetail = async (assignmentId) => {
    try {
      const response = await api.get(`/case-manager/cases/${assignmentId}`);
      setCaseDetail(response.data);
      setSelectedCase(assignmentId);
    } catch (error) {
      toast.error('Failed to load case details');
    }
  };

  const addCaseNote = async () => {
    if (!newNote || !selectedCase) return;

    try {
      await api.post(`/case-manager/cases/${selectedCase}/notes`,
        { content: newNote, note_type: 'general' }
      );
      toast.success('Note added');
      setNewNote('');
      fetchCaseDetail(selectedCase);
    } catch (error) {
      toast.error('Failed to add note');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
      </div>
    );
  }

  const stats = dashboard?.stats || {};

  return (
    <div className="min-h-screen bg-slate-50" data-testid="case-manager-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
              <ClipboardList className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <h1 className="text-lg sm:text-xl font-bold text-slate-900 font-['Manrope']">Case Manager Portal</h1>
              <p className="text-xs sm:text-sm text-slate-500">{dashboard?.profile?.organization || user?.name}</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => navigate('/settings')}>
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Active Cases</p>
                  <p className="text-2xl font-bold text-slate-900">{stats.active_cases || 0}</p>
                </div>
                <Users className="w-8 h-8 text-blue-500" />
              </div>
              <p className="text-xs text-slate-400 mt-1">Capacity: {stats.caseload_capacity || '0/50'}</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Reviews Due</p>
                  <p className="text-2xl font-bold text-amber-600">{stats.reviews_due || 0}</p>
                </div>
                <Calendar className="w-8 h-8 text-amber-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Pending Alerts</p>
                  <p className="text-2xl font-bold text-rose-600">{stats.pending_alerts || 0}</p>
                </div>
                <AlertTriangle className="w-8 h-8 text-rose-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Compliance</p>
                  <p className="text-2xl font-bold text-green-600">Good</p>
                </div>
                <CheckCircle2 className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Alerts Section */}
        {(dashboard?.alerts?.length || 0) > 0 && (
          <Card className="border-rose-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2 text-rose-700">
                <AlertTriangle className="w-5 h-5" />
                Alerts Requiring Attention
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {dashboard.alerts.slice(0, 5).map((alert, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-rose-50 rounded-lg">
                    <div>
                      <p className="font-medium text-rose-800">{alert.title || alert.type}</p>
                      <p className="text-sm text-rose-600">{alert.message}</p>
                    </div>
                    <Button size="sm" variant="outline">Review</Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Main Content */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Cases List */}
          <div className="lg:col-span-1 space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">My Cases</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y max-h-[500px] overflow-auto">
                  {(dashboard?.cases || []).map((caseItem) => (
                    <button
                      key={caseItem.assignment_id}
                      onClick={() => fetchCaseDetail(caseItem.assignment_id)}
                      className={`w-full p-4 text-left hover:bg-slate-50 transition-colors ${
                        selectedCase === caseItem.assignment_id ? 'bg-teal-50 border-l-4 border-teal-500' : ''
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-slate-900">{caseItem.family_name || 'Family'}</p>
                          <p className="text-sm text-slate-500">{caseItem.care_recipient || 'Care Recipient'}</p>
                        </div>
                        <Badge variant={caseItem.priority === 'high' ? 'destructive' : 'secondary'}>
                          {caseItem.priority || 'normal'}
                        </Badge>
                      </div>
                    </button>
                  ))}

                  {(dashboard?.cases?.length || 0) === 0 && (
                    <div className="p-8 text-center text-slate-500">
                      <ClipboardList className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                      <p>No cases assigned</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Case Detail */}
          <div className="lg:col-span-2">
            {caseDetail ? (
              <div className="space-y-4">
                {/* Case Header */}
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div>
                        <h2 className="text-xl font-bold text-slate-900">
                          {caseDetail.family?.name || 'Family'}
                        </h2>
                        <p className="text-slate-500">{caseDetail.family?.email}</p>
                        {caseDetail.care_profile && (
                          <div className="mt-3">
                            <p className="text-sm font-medium text-slate-700">
                              Care Recipient: {caseDetail.care_profile.care_recipient_name}
                            </p>
                            <p className="text-sm text-slate-500">
                              Age: {caseDetail.care_profile.age} • {caseDetail.care_profile.mobility_level}
                            </p>
                            {caseDetail.care_profile.conditions?.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {caseDetail.care_profile.conditions.map((c, i) => (
                                  <Badge key={i} variant="secondary" className="text-xs">{c}</Badge>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      <Button>
                        <FileText className="w-4 h-4 mr-2" />
                        New Review
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Tabs */}
                <Tabs defaultValue="notes" className="space-y-4">
                  <TabsList>
                    <TabsTrigger value="notes">Case Notes</TabsTrigger>
                    <TabsTrigger value="shifts">Care Activity</TabsTrigger>
                    <TabsTrigger value="circle">Care Circle</TabsTrigger>
                  </TabsList>

                  <TabsContent value="notes">
                    <Card>
                      <CardContent className="p-4 space-y-4">
                        {/* Add Note */}
                        <div className="space-y-2">
                          <Textarea
                            placeholder="Add a case note..."
                            value={newNote}
                            onChange={(e) => setNewNote(e.target.value)}
                            rows={3}
                          />
                          <Button onClick={addCaseNote} disabled={!newNote}>Add Note</Button>
                        </div>

                        {/* Notes List */}
                        <div className="space-y-3 pt-4 border-t">
                          {(caseDetail.notes || []).map((note) => (
                            <div key={note.note_id} className="p-3 bg-slate-50 rounded-lg">
                              <p className="text-slate-700">{note.content}</p>
                              <p className="text-xs text-slate-400 mt-2">
                                {new Date(note.created_at).toLocaleString()} • {note.note_type}
                              </p>
                            </div>
                          ))}
                          {(caseDetail.notes?.length || 0) === 0 && (
                            <p className="text-center text-slate-400 py-4">No notes yet</p>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>

                  <TabsContent value="shifts">
                    <Card>
                      <CardContent className="p-4">
                        {(caseDetail.recent_shifts?.length || 0) === 0 ? (
                          <div className="text-center py-8 text-slate-500">
                            <Clock className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                            <p>No recent care activity</p>
                          </div>
                        ) : (
                          <div className="space-y-3">
                            {caseDetail.recent_shifts.map((shift) => (
                              <div key={shift.shift_id} className="flex items-center justify-between p-3 border rounded-lg">
                                <div>
                                  <p className="font-medium">{shift.caregiver_id}</p>
                                  <p className="text-sm text-slate-500">
                                    {new Date(shift.clock_in).toLocaleDateString()}
                                  </p>
                                </div>
                                <div className="text-right">
                                  <p className="font-medium">{shift.total_hours?.toFixed(1) || '—'} hrs</p>
                                  <Badge>{shift.status}</Badge>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </TabsContent>

                  <TabsContent value="circle">
                    <Card>
                      <CardContent className="p-4">
                        {caseDetail.care_circle ? (
                          <div className="space-y-4">
                            <h4 className="font-medium">{caseDetail.care_circle.name}</h4>
                            <div className="grid grid-cols-2 gap-3">
                              {(caseDetail.care_circle.members || []).map((member, idx) => (
                                <div key={idx} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                                  <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center">
                                    {member.name?.charAt(0) || '?'}
                                  </div>
                                  <div>
                                    <p className="font-medium text-sm">{member.name}</p>
                                    <p className="text-xs text-slate-500">{member.role}</p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <div className="text-center py-8 text-slate-500">
                            <Users className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                            <p>No care circle set up</p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </TabsContent>
                </Tabs>
              </div>
            ) : (
              <Card>
                <CardContent className="py-16 text-center">
                  <Eye className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p className="text-slate-500">Select a case to view details</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
